import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  Palmtree,
  Plus,
  Filter,
  GripVertical,
  Star,
  X
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Planning = () => {
  const { isAdmin, isSupervisor, currentOrganization } = useAuth();
  const { t, language } = useLanguage();

  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  // Filter
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [departments, setDepartments] = useState([]);

  // Shift assignment mode
  const [selectedShiftTemplate, setSelectedShiftTemplate] = useState('');

  // Vacation dialog
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [vacationForm, setVacationForm] = useState({
    employee_id: '',
    start_date: '',
    end_date: '',
    notes: ''
  });

  // Clear data when organization changes to prevent stale data
  useEffect(() => {
    setEmployees([]);
    setAssignments([]);
    setVacations([]);
    setAbsences([]);
    setTemplates([]);
    setHolidays([]);
    setDepartments([]);
    setLoading(true);
  }, [currentOrganization?.id]);

  useEffect(() => {
    fetchData();
  }, [currentWeekStart, currentOrganization?.id]);

  const fetchData = async () => {
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const startStr = formatDateString(currentWeekStart);
      const endStr = formatDateString(weekEnd);

      const [employeesRes, assignmentsRes, vacationsRes, templatesRes, absencesRes, calendarsRes] = await Promise.all([
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/shifts/assignments?start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API_URL}/vacations`),
        axios.get(`${API_URL}/shifts/templates`),
        axios.get(`${API_URL}/absences`),
        axios.get(`${API_URL}/calendars`)
      ]);

      setEmployees(employeesRes.data.filter(e => e.is_active !== false));
      setAssignments(assignmentsRes.data);
      setVacations(vacationsRes.data.filter(v => v.status === 'approved'));
      setAbsences(absencesRes.data);
      setTemplates(templatesRes.data);

      // Fetch holidays from each calendar
      const allHolidays = [];
      for (const calendar of calendarsRes.data) {
        try {
          const holidaysRes = await axios.get(`${API_URL}/calendars/${calendar.id}/holidays`);
          holidaysRes.data.forEach(h => {
            allHolidays.push({
              date: h.date,
              name: h.name || (language === 'es' ? 'Festivo' : 'Holiday'),
              calendarName: calendar.name
            });
          });
        } catch (err) {
          console.log(`No holidays for calendar ${calendar.id}`);
        }
      }
      setHolidays(allHolidays);

      // Extract unique departments
      const uniqueDepts = [...new Set(employeesRes.data.map(e => e.department).filter(Boolean))];
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Memoize weekDays to ensure consistent calculation
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [currentWeekStart]);

  // Memoize week end date for header
  const weekEnd = useMemo(() => {
    const end = new Date(currentWeekStart);
    end.setDate(currentWeekStart.getDate() + 6);
    return end;
  }, [currentWeekStart]);

  const navigateWeek = useCallback((direction) => {
    setCurrentWeekStart(prev => {
      const newStart = new Date(prev);
      newStart.setDate(newStart.getDate() + (direction * 7));
      return newStart;
    });
  }, []);

  const goToToday = useCallback(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  }, []);

  const getAssignmentForDate = (employeeId, date) => {
    const dateStr = formatDateString(date);
    return assignments.find(a => a.employee_id === employeeId && a.date === dateStr);
  };

  const getVacationForDate = (employeeId, date) => {
    const dateStr = formatDateString(date);
    return vacations.find(v =>
      v.employee_id === employeeId &&
      dateStr >= v.start_date &&
      dateStr <= v.end_date
    );
  };

  const getAbsenceForDate = (employeeId, date) => {
    const dateStr = formatDateString(date);
    return absences.find(a =>
      a.employee_id === employeeId &&
      dateStr >= a.start_date &&
      dateStr <= a.end_date
    );
  };

  const getHolidayForDate = (date) => {
    const dateStr = formatDateString(date);
    return holidays.find(h => h.date === dateStr);
  };

  const getTemplateById = (templateId) => {
    return templates.find(t => t.id === templateId);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isWeekend = (date) => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const filteredEmployees = employees.filter(emp => {
    if (emp.is_active === false) return false;
    if (emp.role === 'inspector') return false;
    if (departmentFilter === 'all') return true;
    return emp.department === departmentFilter;
  });

  // Remove a shift assignment
  const handleRemoveShift = async (assignment, e) => {
    if (e) e.stopPropagation();
    if (!isAdmin() && !isSupervisor()) return;
    try {
      await axios.delete(`${API_URL}/shifts/assignments/${assignment.id}`);
      toast.success(language === 'es' ? 'Turno eliminado' : 'Shift removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleCellClick = async (employee, date) => {
    if (!isAdmin() && !isSupervisor()) return;

    const dateStr = formatDateString(date);
    const existingAssignment = getAssignmentForDate(employee.id, date);

    if (selectedShiftTemplate) {
      // Shift assignment mode
      if (existingAssignment && existingAssignment.shift_template_id === selectedShiftTemplate) {
        // Same template → toggle off (remove)
        await handleRemoveShift(existingAssignment);
      } else if (existingAssignment) {
        // Different template → replace with confirmation
        const oldName = getTemplateById(existingAssignment.shift_template_id)?.name || '?';
        const newName = getTemplateById(selectedShiftTemplate)?.name || '?';
        if (!window.confirm(language === 'es'
          ? `¿Reemplazar "${oldName}" por "${newName}" para ${employee.name} el ${dateStr}?`
          : `Replace "${oldName}" with "${newName}" for ${employee.name} on ${dateStr}?`)) return;
        try {
          await axios.delete(`${API_URL}/shifts/assignments/${existingAssignment.id}`);
          await axios.post(`${API_URL}/shifts/assignments`, {
            employee_id: employee.id,
            shift_template_id: selectedShiftTemplate,
            date: dateStr
          });
          toast.success(language === 'es' ? 'Turno asignado' : 'Shift assigned');
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error_occurred'));
        }
      } else {
        // Empty cell → assign
        try {
          await axios.post(`${API_URL}/shifts/assignments`, {
            employee_id: employee.id,
            shift_template_id: selectedShiftTemplate,
            date: dateStr
          });
          toast.success(language === 'es' ? 'Turno asignado' : 'Shift assigned');
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || t('error_occurred'));
        }
      }
      return;
    }

    // Vacation mode (no template selected, only on empty cells)
    if (existingAssignment) return;
    setSelectedEmployee(employee);
    setSelectedDate(date);
    setVacationForm({
      employee_id: employee.id,
      start_date: dateStr,
      end_date: dateStr,
      notes: ''
    });
    setVacationDialogOpen(true);
  };

  const handleCreateVacation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/vacations`, {
        ...vacationForm,
        status: 'approved'
      });
      toast.success(language === 'es' ? 'Vacaciones asignadas' : 'Vacation assigned');
      setVacationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, assignment, employee, date) => {
    if (!isAdmin() && !isSupervisor()) return;

    const template = getTemplateById(assignment.shift_template_id);
    setDraggedItem({
      assignment,
      sourceEmployee: employee,
      sourceDate: date,
      template
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', assignment.id);
  };

  const handleDragOver = (e, employee, date) => {
    e.preventDefault();
    if (!draggedItem) return;

    const dateStr = formatDateString(date);
    const cellKey = `${employee.id}-${dateStr}`;

    const vacation = getVacationForDate(employee.id, date);
    const absence = getAbsenceForDate(employee.id, date);
    if (vacation || absence) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(cellKey);
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (e, targetEmployee, targetDate) => {
    e.preventDefault();
    setDragOverCell(null);

    if (!draggedItem || (!isAdmin() && !isSupervisor())) {
      setDraggedItem(null);
      return;
    }

    const targetDateStr = formatDateString(targetDate);
    const sourceDateStr = formatDateString(draggedItem.sourceDate);

    if (targetEmployee.id === draggedItem.sourceEmployee.id && targetDateStr === sourceDateStr) {
      setDraggedItem(null);
      return;
    }

    const vacation = getVacationForDate(targetEmployee.id, targetDate);
    const absence = getAbsenceForDate(targetEmployee.id, targetDate);
    if (vacation || absence) {
      toast.error(language === 'es' ? 'No se puede mover a un día de vacaciones o ausencia' : 'Cannot move to a vacation or absence day');
      setDraggedItem(null);
      return;
    }

    const confirmMessage = targetEmployee.id !== draggedItem.sourceEmployee.id
      ? (language === 'es'
          ? `¿Mover turno "${draggedItem.template?.name}" de ${draggedItem.sourceEmployee.name} a ${targetEmployee.name} el ${targetDateStr}?`
          : `Move shift "${draggedItem.template?.name}" from ${draggedItem.sourceEmployee.name} to ${targetEmployee.name} on ${targetDateStr}?`)
      : (language === 'es'
          ? `¿Mover turno "${draggedItem.template?.name}" del ${sourceDateStr} al ${targetDateStr}?`
          : `Move shift "${draggedItem.template?.name}" from ${sourceDateStr} to ${targetDateStr}?`);

    if (!window.confirm(confirmMessage)) {
      setDraggedItem(null);
      return;
    }

    try {
      await axios.delete(`${API_URL}/shifts/assignments/${draggedItem.assignment.id}`);
      await axios.post(`${API_URL}/shifts/assignments`, {
        employee_id: targetEmployee.id,
        shift_template_id: draggedItem.assignment.shift_template_id,
        date: targetDateStr
      });

      toast.success(language === 'es' ? 'Turno movido correctamente' : 'Shift moved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    } finally {
      setDraggedItem(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverCell(null);
  };

  const dayNames = language === 'es'
    ? ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const activeTpl = selectedShiftTemplate ? getTemplateById(selectedShiftTemplate) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
            {language === 'es' ? 'Planning' : 'Planning'}
          </h1>
          <p className="text-slate-500">
            {language === 'es'
              ? 'Vista consolidada de turnos y vacaciones de todos los empleados'
              : 'Consolidated view of shifts and vacations for all employees'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Shift assignment mode selector */}
          {(isAdmin() || isSupervisor()) && (
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-slate-400" />
              <Select
                value={selectedShiftTemplate || 'none'}
                onValueChange={(v) => setSelectedShiftTemplate(v === 'none' ? '' : v)}
              >
                <SelectTrigger className={`w-[200px] ${activeTpl ? 'ring-2 ring-indigo-400 border-indigo-400' : ''}`}>
                  <SelectValue>
                    {activeTpl ? (
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: activeTpl.color }} />
                        {activeTpl.name}
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        {language === 'es' ? 'Asignar turno…' : 'Assign shift…'}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-slate-400">{language === 'es' ? '— Sin modo asignación —' : '— No assignment mode —'}</span>
                  </SelectItem>
                  {templates.map(tpl => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tpl.color }} />
                        {tpl.name} ({tpl.start_time}–{tpl.end_time})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeTpl && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-slate-700 px-1"
                  onClick={() => setSelectedShiftTemplate('')}
                  title={language === 'es' ? 'Cancelar modo asignación' : 'Cancel assignment mode'}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}

          {/* Department Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={language === 'es' ? 'Departamento' : 'Department'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Assignment mode banner */}
      {activeTpl && (
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 text-sm">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: activeTpl.color }} />
          <span className="font-medium">
            {language === 'es'
              ? `Modo asignación: ${activeTpl.name} — haz click en una celda para asignar, click en el mismo turno para eliminarlo`
              : `Assignment mode: ${activeTpl.name} — click a cell to assign, click the same shift to remove`}
          </span>
        </div>
      )}

      {/* Week Navigation */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="planning-prev-week">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} data-testid="planning-today">
                {language === 'es' ? 'Hoy' : 'Today'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="planning-next-week">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <CardTitle className="font-['Manrope'] text-lg" data-testid="planning-week-header">
              {currentWeekStart.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                month: 'long',
                year: 'numeric',
                day: 'numeric'
              })} - {weekEnd.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                day: 'numeric',
                month: 'long'
              })}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Users className="w-4 h-4" />
              {filteredEmployees.length} {language === 'es' ? 'empleados' : 'employees'}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {/* Planning Grid */}
          <div className="min-w-[800px]">
            {/* Header Row */}
            <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50">
              <div className="p-3 font-medium text-slate-700 border-r border-slate-200">
                {language === 'es' ? 'Empleado' : 'Employee'}
              </div>
              {weekDays.map((day, index) => {
                const holiday = getHolidayForDate(day);
                return (
                  <div
                    key={index}
                    className={`p-2 text-center border-r border-slate-200 last:border-r-0 ${
                      isToday(day) ? 'bg-indigo-100' : holiday ? 'bg-red-50' : isWeekend(day) ? 'bg-slate-100' : ''
                    }`}
                    title={holiday ? holiday.name : ''}
                  >
                    <div className="text-xs font-medium text-slate-500 flex items-center justify-center gap-1">
                      {dayNames[index]}
                      {holiday && <Star className="w-3 h-3 text-red-500 fill-red-500" />}
                    </div>
                    <div className={`text-lg font-semibold ${isToday(day) ? 'text-indigo-600' : holiday ? 'text-red-600' : 'text-slate-700'}`}>
                      {day.getDate()}
                    </div>
                    {holiday && (
                      <div className="text-[9px] text-red-600 truncate px-1" title={holiday.name}>
                        {holiday.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Employee Rows */}
            {filteredEmployees.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                {language === 'es' ? 'No hay empleados para mostrar' : 'No employees to display'}
              </div>
            ) : (
              filteredEmployees.map(employee => (
                <div key={employee.id} className="grid grid-cols-8 border-b border-slate-100 hover:bg-slate-50/50">
                  {/* Employee Name */}
                  <div className="p-3 border-r border-slate-200 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-medium text-sm flex-shrink-0">
                      {employee.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate text-sm">{employee.name}</div>
                      {employee.department && (
                        <div className="text-xs text-slate-500 truncate">{employee.department}</div>
                      )}
                    </div>
                  </div>

                  {/* Day Cells */}
                  {weekDays.map((day, dayIndex) => {
                    const assignment = getAssignmentForDate(employee.id, day);
                    const vacation = getVacationForDate(employee.id, day);
                    const absence = getAbsenceForDate(employee.id, day);
                    const holiday = getHolidayForDate(day);
                    const template = assignment ? getTemplateById(assignment.shift_template_id) : null;
                    const dateStr = formatDateString(day);
                    const cellKey = `${employee.id}-${dateStr}`;
                    const isDragOver = dragOverCell === cellKey;
                    const canEdit = (isAdmin() || isSupervisor()) && !vacation && !absence;
                    const isAssignMode = !!selectedShiftTemplate;

                    // Cursor: clickable in assign mode (any cell without vacation/absence),
                    // or in vacation mode (only empty cells)
                    const isClickable = canEdit && (isAssignMode || !template);

                    return (
                      <div
                        key={dayIndex}
                        className={`p-1 border-r border-slate-200 last:border-r-0 min-h-[60px] transition-all ${
                          isToday(day) ? 'bg-indigo-50/50' : holiday ? 'bg-red-50/30' : isWeekend(day) ? 'bg-slate-50' : ''
                        } ${isDragOver && canEdit ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-inset' : ''} ${
                          isClickable
                            ? isAssignMode
                              ? 'cursor-pointer hover:bg-indigo-50'
                              : 'cursor-pointer hover:bg-indigo-50'
                            : ''
                        }`}
                        onClick={() => {
                          if (absence || vacation) return;
                          if (isAssignMode || !template) handleCellClick(employee, day);
                        }}
                        onDragOver={(e) => handleDragOver(e, employee, day)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, employee, day)}
                        title={
                          isAssignMode && canEdit
                            ? template
                              ? (language === 'es' ? `Click para reemplazar/eliminar • Arrastra para mover` : `Click to replace/remove • Drag to move`)
                              : (language === 'es' ? `Click para asignar "${activeTpl?.name}"` : `Click to assign "${activeTpl?.name}"`)
                            : template
                              ? (language === 'es' ? 'Arrastra para mover el turno' : 'Drag to move shift')
                              : absence
                                ? (language === 'es' ? `Ausencia: ${absence.absence_type || 'Baja'}` : `Absence: ${absence.absence_type || 'Leave'}`)
                                : holiday
                                  ? holiday.name
                                  : (language === 'es' ? 'Click para asignar vacaciones' : 'Click to assign vacation')
                        }
                      >
                        {vacation ? (
                          <div className="h-full flex items-center justify-center">
                            <Badge className="bg-green-500 text-white text-[10px] px-1.5 py-0.5">
                              <Palmtree className="w-3 h-3 mr-1" />
                              {language === 'es' ? 'Vac.' : 'Vac.'}
                            </Badge>
                          </div>
                        ) : absence ? (
                          <div className="h-full flex items-center justify-center">
                            <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0.5">
                              {language === 'es' ? 'Baja' : 'Leave'}
                            </Badge>
                          </div>
                        ) : template ? (
                          <div
                            className={`h-full rounded px-1 py-0.5 text-[10px] text-white flex flex-col justify-center relative group ${
                              (isAdmin() || isSupervisor()) && !isAssignMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                            } ${
                              isAssignMode && assignment?.shift_template_id === selectedShiftTemplate
                                ? 'ring-2 ring-white ring-inset opacity-80'
                                : ''
                            }`}
                            style={{ backgroundColor: template.color || '#6366f1' }}
                            draggable={!isAssignMode && (isAdmin() || isSupervisor())}
                            onDragStart={(e) => !isAssignMode && handleDragStart(e, assignment, employee, day)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="flex items-center gap-0.5">
                              {(isAdmin() || isSupervisor()) && !isAssignMode && (
                                <GripVertical className="w-3 h-3 opacity-50 flex-shrink-0" />
                              )}
                              <span className="font-medium truncate">{template.name}</span>
                            </div>
                            <div className="opacity-80 pl-3.5">{template.start_time?.slice(0,5)}-{template.end_time?.slice(0,5)}</div>
                            {/* X delete button — shown on hover when NOT in assign mode */}
                            {(isAdmin() || isSupervisor()) && !isAssignMode && (
                              <button
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 hidden group-hover:flex items-center justify-center z-10 text-[10px] leading-none"
                                onClick={(e) => handleRemoveShift(assignment, e)}
                                title={language === 'es' ? 'Eliminar turno' : 'Remove shift'}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center">
                            {isAssignMode && canEdit ? (
                              <div className="w-full h-full flex items-center justify-center rounded border border-dashed border-indigo-300 text-indigo-300 text-xs">
                                <Plus className="w-3 h-3" />
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">-</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <span className="text-sm font-medium text-slate-700">
                {language === 'es' ? 'Leyenda:' : 'Legend:'}
              </span>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-500 text-white">
                  <Palmtree className="w-3 h-3 mr-1" />
                  {language === 'es' ? 'Vacaciones' : 'Vacation'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-500 text-white">
                  {language === 'es' ? 'Baja/Ausencia' : 'Leave/Absence'}
                </Badge>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-100 border border-red-300 flex items-center justify-center">
                  <Star className="w-2 h-2 text-red-500 fill-red-500" />
                </div>
                <span className="text-xs text-slate-600">{language === 'es' ? 'Festivo' : 'Holiday'}</span>
              </div>
              {templates.slice(0, 5).map(template => (
                <div key={template.id} className="flex items-center gap-1">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: template.color || '#6366f1' }}
                  />
                  <span className="text-xs text-slate-600">{template.name}</span>
                </div>
              ))}
            </div>
            {(isAdmin() || isSupervisor()) && (
              <div className="flex flex-col gap-1.5 items-end text-xs text-slate-500">
                {!selectedShiftTemplate && (
                  <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full">
                    <GripVertical className="w-3 h-3" />
                    {language === 'es'
                      ? 'Arrastra los turnos para moverlos entre días o empleados'
                      : 'Drag shifts to move them between days or employees'}
                  </div>
                )}
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full">
                  <CalendarIcon className="w-3 h-3" />
                  {language === 'es'
                    ? 'Selecciona un turno arriba para asignarlo con click'
                    : 'Select a shift above to assign it by clicking'}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vacation Assignment Dialog */}
      <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'es' ? 'Asignar Vacaciones' : 'Assign Vacation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateVacation} className="space-y-4">
            {selectedEmployee && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{language === 'es' ? 'Empleado' : 'Employee'}</p>
                <p className="font-medium">{selectedEmployee.name}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Fecha inicio' : 'Start date'}</Label>
                <Input
                  type="date"
                  value={vacationForm.start_date}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Fecha fin' : 'End date'}</Label>
                <Input
                  type="date"
                  value={vacationForm.end_date}
                  onChange={(e) => setVacationForm(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'es' ? 'Notas (opcional)' : 'Notes (optional)'}</Label>
              <Input
                value={vacationForm.notes}
                onChange={(e) => setVacationForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'es' ? 'Motivo de las vacaciones...' : 'Vacation reason...'}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setVacationDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Asignar Vacaciones' : 'Assign Vacation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Planning;
